import type { LayerType } from '../types';

/**
 * Activity Meters - Visual feedback for layer triggers
 * Minimal monochrome design showing real-time grain activity
 */

interface LayerMeterConfig {
  type: LayerType;
  label: string;
}

const LAYER_CONFIGS: LayerMeterConfig[] = [
  { type: 'percussion', label: 'PERC' },
  { type: 'bass', label: 'BASS' },
  { type: 'melody', label: 'MELO' },
  { type: 'pad', label: 'PAD' },
];

export class ActivityMeters {
  private container: HTMLElement | null = null;
  private meters: Map<LayerType, HTMLElement> = new Map();
  private decayTimeouts: Map<LayerType, number> = new Map();

  // Animation settings
  private readonly decayTime = 150; // ms for meter to fade

  constructor() {}

  /**
   * Initialize meters and attach to container
   */
  init(containerId: string): void {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error('Activity meters container not found:', containerId);
      return;
    }

    this.container = container;

    // Create meter elements
    const metersWrapper = document.createElement('div');
    metersWrapper.className = 'activity-meters flex justify-center gap-6';

    LAYER_CONFIGS.forEach(({ type, label }) => {
      const meterEl = document.createElement('div');
      meterEl.className = 'activity-meter text-center';
      meterEl.innerHTML = `
        <div class="meter-bar-container relative w-8 h-16 mx-auto mb-2 rounded overflow-hidden" style="background: var(--color-muted);">
          <div
            class="meter-bar absolute bottom-0 left-0 right-0 transition-all duration-75"
            style="height: 0%; background: var(--color-foreground);"
          ></div>
        </div>
        <div class="text-xs font-medium tracking-wider" style="color: var(--color-muted-foreground);">
          ${label}
        </div>
      `;

      metersWrapper.appendChild(meterEl);
      this.meters.set(type, meterEl.querySelector('.meter-bar') as HTMLElement);
    });

    container.appendChild(metersWrapper);

    // Listen for grain triggers
    this.setupListeners();
  }

  /**
   * Setup listeners for grain triggers
   */
  private setupListeners(): void {
    // Listen for custom events from grain layers
    window.addEventListener('grain:trigger', ((event: CustomEvent) => {
      const { type, velocity } = event.detail;
      this.trigger(type, velocity);
    }) as EventListener);
  }

  /**
   * Trigger meter flash for a layer
   */
  trigger(type: LayerType, velocity = 1): void {
    const meterBar = this.meters.get(type);
    if (!meterBar) return;

    // Clear any existing decay
    const existingTimeout = this.decayTimeouts.get(type);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Flash to full height (scaled by velocity)
    const height = Math.min(100, velocity * 100);
    meterBar.style.height = `${height}%`;

    // Schedule decay
    const timeout = window.setTimeout(() => {
      meterBar.style.height = '0%';
    }, this.decayTime);

    this.decayTimeouts.set(type, timeout);
  }

  /**
   * Reset all meters
   */
  reset(): void {
    this.meters.forEach((meterBar) => {
      meterBar.style.height = '0%';
    });

    this.decayTimeouts.forEach((timeout) => {
      clearTimeout(timeout);
    });
    this.decayTimeouts.clear();
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.reset();
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.meters.clear();
  }
}

/**
 * Emit grain trigger event (call from GrainLayer)
 */
export function emitGrainTrigger(type: LayerType, velocity = 1): void {
  window.dispatchEvent(
    new CustomEvent('grain:trigger', {
      detail: { type, velocity },
    })
  );
}
